<template>
  <div class="search container">
    <div v-if="shouldShowSearchResults">
      <ProductGrid :products="results" layout="grid" narrow show-one-photo />
    </div>
    <div v-else-if="shouldShowRecentSearches" class="search-panel">
      <span class="search-panel__heading">Recent Searches</span>
      <ul class="recent-searches">
        <li
          v-for="search in orderedRecentSearches"
          :key="search.id"
          class="recent-searches__item"
        >
          <button
            type="button"
            class="recent-searches__link"
            @click="handleUpdateSearchTerm(search.value)"
          >
            {{ search.value }}
          </button>

          <IconButton icon="cross" @click="removeRecentSearch(search)" />
        </li>
      </ul>
    </div>
    <div
      v-for="(placement, key) in searchRecommendationsWithStaticHeadings"
      :key="key"
    >
      <NostoProductRecommendationsGrid
        :recommendations="placement.products"
        :placement="key"
        :heading="placement.title"
      />
    </div>
  </div>
</template>

<script>
import searchProductsQuery from "@/graphql/queries/Search.gql";
import { unwrapNodes } from "@/helpers/graphql";
import { mapActions, mapGetters, mapState } from "vuex";
import ProductGrid from "@/components/collection/ProductGrid.vue";
import IconButton from "@/components/IconButton.vue";
import NostoProductRecommendationsGrid from "@/components/NostoProductRecommendationsGrid.vue";

export default {
  name: "SearchRecent",
  components: {
    ProductGrid,
    IconButton,
    NostoProductRecommendationsGrid,
  },
  emits: ["clickedRecentSearch"],
  data() {
    return {
      searchResults: [],
    };
  },
  computed: {
    ...mapState("search", ["recentSearches", "searchTerm"]),
    ...mapGetters("search", [
      "orderedRecentSearches",
      "searchRecommendationsWithStaticHeadings",
    ]),
    shouldShowSearchResults() {
      const searchResultNodes = unwrapNodes(this.searchResults?.edges) || [];
      return !!searchResultNodes.length > 0;
    },
    resultCount() {
      return this.orderedRecentSearches.length;
    },
    results() {
      return unwrapNodes(this.searchResults?.edges) || [];
    },
    shouldShowRecentSearches() {
      return this.recentSearches.length > 0;
    },
  },
  unmounted() {
    this.clearSearchTerm();
  },
  created() {
    this.fetchRecentSearches();
  },
  methods: {
    ...mapActions("search", [
      "fetchRecentSearches",
      "removeRecentSearch",
      "addSearchTerm",
      "clearSearchTerm",
    ]),
    unwrapNodes,
    handleUpdateSearchTerm(val) {
      this.addSearchTerm(val);
      this.$emit("clickedRecentSearch");
    },
  },
  apollo: {
    searchResults: {
      query: searchProductsQuery,
      skip() {
        return !this.searchTerm;
      },
      variables() {
        return {
          searchQuery: this.searchTerm,
        };
      },
      update: (data) => data.products,
    },
  },
};
</script>

<style lang="scss" scoped>
.search-panel {
  padding: 20px 0;

  &__heading {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    margin-bottom: 10px;
    display: block;
  }
}

.recent-searches {
  &__item {
    display: flex;
    justify-content: space-between;
  }

  &__link {
    padding: 10px 0;
    background: none;
    outline: none;
    border: none;
    font-size: 10px;
    display: block;
  }
}

.search-results {
  display: flex;
  overflow-x: scroll;
  white-space: nowrap;

  & > * {
    flex: 1 0 60.5%;
    margin-right: 2px;

    @include respond-above(md) {
      flex: 1 0 40%;
    }
  }
}

.search-accordion {
  margin-left: -20px;
  margin-right: -20px;
  padding-left: 20px;
  padding-right: 20px;
  transition: all 0.3s;
  transition-timing-function: ease-in;

  ::v-deep() {
    .accordion__header {
      transition: padding-top 0.3s;
      transition-timing-function: ease-in;
      align-items: center;
      font-family: $body-font-family;
      text-transform: uppercase;
      width: 100%;
      font-size: 9px;
      background: none;
      outline: none;
      border: none;
      padding: 8px 0;
      font-weight: 600;
    }
  }
}

::v-deep() {
  .search-accordion.accordion--open {
    transition-timing-function: ease-out;
    background-color: $color-light;
    padding-bottom: 10px;
    margin-bottom: 20px;

    .accordion__header {
      transition-timing-function: ease-out;
    }
  }
}
</style>
